import ConfirmationPrompt from 'common/components/ConfirmationPrompt';
import DefaultPageLoader from 'common/components/DefaultPageLoader';
import ErrorComponent from 'common/components/Error';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import apps from 'common/services/apps';
import { CannotDeleteAppAsNonAdminError } from 'common/types/apps';
import React, { useState, useEffect } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useParams, useNavigate } from 'react-router-dom';
import Button from 'common/components/Button';
import Description from 'common/components/Description';

function DeleteApp() {
  const params = useParams();
  const navigate = useNavigate();
  const appId = params.id || 'asdf';

  const [confirmedDelete, setConfirmedDelete] = useState(false);
  const [navigationScheduled, setNavigationScheduled] = useState(false);

  const deleteAppTask = useAsyncCallback(async () => {
    if (!appId || appId === 'asdf') {
      throw new Error("Application ID is missing.");
    }
    try {
      await apps.deleteApp(appId);
      setNavigationScheduled(true);
    } catch (e: any) {
      setConfirmedDelete(false);
      if (e instanceof CannotDeleteAppAsNonAdminError) {
        throw new Error('You must be an admin for your team to delete this app.');
      }
      throw e;
    }
  });

  const getAppTask = useAsync(async () => {
    if (!appId || appId === 'asdf') return null;
    return apps.getApp(appId);
  }, [appId]);
  
  const app = getAppTask.result;

  useEffect(() => {
    if (confirmedDelete && !deleteAppTask.loading && !navigationScheduled) {
      deleteAppTask.execute().catch(() => {
      });
    }
  }, [confirmedDelete, deleteAppTask, navigationScheduled]);

  useEffect(() => {
    if (navigationScheduled) {
      navigate('/');
    }
  }, [navigationScheduled, navigate]);

  if (getAppTask.loading || (confirmedDelete && deleteAppTask.loading) || navigationScheduled) {
    return <DefaultPageLoader />;
  }

  if (getAppTask.error && !confirmedDelete) {
    return <ErrorComponent>{getAppTask.error?.message}</ErrorComponent>;
  }

  if (deleteAppTask.error) {
    return (
      <AuthenticatedLayout pageIdentifier="delete-app-error">
        <ErrorComponent>{deleteAppTask.error.message}</ErrorComponent>
        <Button onClick={() => {
          deleteAppTask.reset();
        }}>OK</Button>
      </AuthenticatedLayout>
    );
  }

  if (app && !confirmedDelete && !navigationScheduled) {
    return (
      <ConfirmationPrompt
        title={`Are you sure you want to delete ${app.name}?`}
        body={
          app.adoptedUserCount > 0 ? (
            <ErrorComponent>
              Deleting this app impacts {app.adoptedUserCount} integrated
              users.
            </ErrorComponent>
          ) : (
            <Description type="info">This app has 0 integrated users.</Description>
          )
        }
        confirmButtonText="Delete App"
        confirmationPhrase="delete app"
        onConfirm={() => {
          setConfirmedDelete(true);
        }}
      />
    );
  }
  
  if (!app && !getAppTask.loading && !confirmedDelete) {
    return <ErrorComponent>Application not found or ID is invalid.</ErrorComponent>;
  }

  return <DefaultPageLoader />;
}

export default () => (
  <AuthenticatedLayout pageIdentifier="delete-app">
    <DeleteApp />
  </AuthenticatedLayout>
);
